<template>
  <section class="babybook-preview">
    <div class="babybook-preview__intro">
      <h2 class="babybook-preview__title">
        Your baby's records,<br>always with you.
        <img class="babybook-preview__title-sparkle" :src="sparkleIcon" alt="">
      </h2>
      <p class="babybook-preview__body">
        Track your baby's growth, vaccinations, medical records, and more in one place. Access your baby's Digital Baby Book on the <strong>NowServing app</strong>.
      </p>
    </div>

    <div class="babybook-preview__widgets">
      <div class="babybook-preview__growth">
        <div class="babybook-preview__growth-header">
          <span class="babybook-preview__growth-title">Growth Summary</span>
        </div>
        <div class="babybook-preview__growth-stats">
          <div class="babybook-preview__stats-wrapper">
            <div class="babybook-preview__stat">
              <span class="babybook-preview__stat-label">Height</span>
              <span class="babybook-preview__stat-value">61.04 cm</span>
            </div>
            <span class="babybook-preview__stat-divider" aria-hidden="true"/>
            <div class="babybook-preview__stat">
              <span class="babybook-preview__stat-label">Weight</span>
              <span class="babybook-preview__stat-value">6 kg</span>
            </div>
          </div>
          <div class="babybook-preview__hero-wrapper">
            <img class="babybook-preview__hero" :src="heroBaby" alt="">
          </div>
        </div>
      </div>

      <div class="babybook-preview__row">
        <div class="babybook-preview__tile babybook-preview__tile--vaccines">
          <div class="babybook-preview__tile-header">
            <img class="babybook-preview__tile-icon" :src="syringeIcon" alt="">
            <span class="babybook-preview__tile-title">Vaccines</span>
          </div>
          <div class="babybook-preview__tile-body">
            <span class="babybook-preview__tile-label">Next Vaccine</span>
            <span class="babybook-preview__tile-value-row">
              <img class="babybook-preview__tile-info" :src="infoIcon" alt="">
              <span class="babybook-preview__tile-value babybook-preview__tile-value--purple">Due Jan 2026</span>
            </span>
          </div>
          <span class="babybook-preview__tile-chevron">
            <img :src="chevronPurple" alt="">
          </span>
        </div>
        <div class="babybook-preview__tile babybook-preview__tile--consultations">
          <div class="babybook-preview__tile-header">
            <img class="babybook-preview__tile-icon" :src="historyIcon" alt="">
            <span class="babybook-preview__tile-title">Consultations &amp; Files</span>
          </div>
          <div class="babybook-preview__tile-body">
            <span class="babybook-preview__tile-label">Last Visit</span>
            <span class="babybook-preview__tile-value babybook-preview__tile-value--blue">Jan 20, 2026</span>
          </div>
          <span class="babybook-preview__tile-chevron">
            <img :src="chevronBlue" alt="">
          </span>
        </div>
      </div>
    </div>

    <div class="flex-row-center-xy-spaced">
      <button type="button" class="babybook-preview__cta" @click="downloadApp">
        Download Now
      </button>
    </div>
  </section>
</template>

<script>
  import {Reference} from 'smd-vue-scaffold';
  import {trackEvent} from '@/utils/logger';
  import GTAG_EVENT_ACTION from '@/reference/gtag-event-actions-lookup';
  import {GTAG_PARAMS_TYPE, GTAG_PARAMS_ORIGIN} from '@/reference/gtag-params-lookup';
  import DownloadAppModal from '@/components/clinic-qr/download-app-modal';
  import HeroBaby from '@/assets/icons/babybook-baby.svg';
  import SyringeIcon from '@/assets/icons/babybook-syringe.svg';
  import HistoryIcon from '@/assets/icons/babybook-history.svg';
  import SparkleIcon from '@/assets/icons/babybook-sparkle.svg';
  import InfoIcon from '@/assets/icons/babybook-tile-info.svg';
  import ChevronPurple from '@/assets/icons/babybook-chevron-purple.svg';
  import ChevronBlue from '@/assets/icons/babybook-chevron-blue.svg';

  const APP_STORE_URL = 'https://apps.apple.com/app/apple-store/id1490833154?pt=95629810&ct=nsp_bb_preview&mt=8';
  const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.leapfroggr.nowserving&referrer=utm_source%3Dnsportal%26utm_campaign%3Dnsp_bb_preview';

  export default {
    name: 'babybook-preview',
    data() {
      return {
        heroBaby: HeroBaby,
        syringeIcon: SyringeIcon,
        historyIcon: HistoryIcon,
        sparkleIcon: SparkleIcon,
        infoIcon: InfoIcon,
        chevronPurple: ChevronPurple,
        chevronBlue: ChevronBlue,
      };
    },
    methods: {
      resolveMobileTarget() {
        const nav = typeof navigator !== 'undefined' ? navigator : null;
        const ua = (nav && nav.userAgent) || '';
        const isIpadOS = nav && /Macintosh/.test(ua) && nav.maxTouchPoints > 1;
        if (/iPad|iPhone|iPod/.test(ua) || isIpadOS) {
          return {url: APP_STORE_URL, source: GTAG_PARAMS_ORIGIN.BabybookPreview_AppStore};
        }
        if (/Android/i.test(ua)) {
          return {url: PLAY_STORE_URL, source: GTAG_PARAMS_ORIGIN.BabybookPreview_PlayStore};
        }
        return null;
      },
      trackDownloadClick(source) {
        const payload = {
          type: GTAG_PARAMS_TYPE.BabybookPreview,
          source,
        };
        this.$gtag.event(GTAG_EVENT_ACTION.Click, payload);
        trackEvent(GTAG_EVENT_ACTION.Click, payload);
      },
      openDownloadChooser() {
        this.trackDownloadClick(GTAG_PARAMS_ORIGIN.BabybookPreview_Web);
        this.$modal.open({
          component: DownloadAppModal,
          size: Reference.SmdModalSizes.xs,
          position: 'detached auto-height',
        });
      },
      downloadApp() {
        const mobileTarget = this.resolveMobileTarget();
        if (mobileTarget) {
          this.trackDownloadClick(mobileTarget.source);
          window.open(mobileTarget.url, '_blank', 'noopener');
          return;
        }
        this.openDownloadChooser();
      },
    },
  };
</script>

<style lang="scss">
  .babybook-preview {
    @extend %layout--flex-col;
    @include border-radius(20px);
    gap: space(m-base);
    padding: space(m-base) space(m);
    background-image: linear-gradient(137.96deg, #ffffff 20.943%, #dfe0ff 78.949%);

    &__intro {
      @extend %layout--flex-col;
      gap: space(xs);
      max-width: 296px;
      color: #24266e;
    }

    &__title {
      @extend %smd-font-family-bold;
      position: relative;
      margin: 0;
      font-size: font(xl);
      line-height: 1.1;
      color: inherit;
      text-transform: none;
    }

    &__title-sparkle {
      position: absolute;
      top: 12px;
      right: 22px;
      width: 38px;
      height: 35px;
      pointer-events: none;
    }

    &__body {
      @extend %smd-font-family;
      margin: 0;
      font-size: 13px;
      line-height: 17px;
      color: inherit;
      text-transform: none;

      strong {
        @extend %smd-font-family-bold;
      }
    }

    &__widgets {
      @extend %layout--flex-col;
      @include border-radius(14px);
      position: relative;
      gap: 8px;
      padding: space(m) 14px;
      background: $color-white;
    }

    &__hero-wrapper {
      position: relative;
      width: 60px;
    }

    &__hero {
      position: absolute;
      top: -118px;
      right: -49px;
      width: 112px;
      height: auto;
      pointer-events: none;
    }

    &__growth {
      @extend %layout--flex-col;
    }

    &__growth-header {
      @include border-radius(radius(normal) radius(normal) 0 0);
      background: #daffee;
      padding: space(s);

      .babybook-preview__growth-title {
        @extend %smd-font-family-bold;
        font-size: 14px;
        color: #054125;
        text-transform: none;
      }
    }

    &__stats-wrapper {
      @extend %layout--flex-row-center-xy;
      gap: space(base);
    }

    &__growth-stats {
      @extend %layout--flex-row-center-x;
      @include border-radius(0 0 radius(normal) radius(normal));
      position: relative;
      background: #ecfff6;
      padding: 12px space(s);
      justify-content: space-between;
      gap: space(base);
      min-height: 72px;

      @include responsive() {
        justify-content: center;
      }
    }

    &__growth-icon {
      width: 34px;
      height: auto;
    }

    &__stat {
      @extend %layout--flex-col-center-y;
      gap: 6px;
    }

    &__stat-label {
      @extend %smd-font-family;
      font-size: 12px;
      color: #618676;
      text-transform: none;
    }

    &__stat-value {
      @extend %smd-font-family-bold;
      font-size: 17px;
      color: #274136;
      text-transform: none;
    }

    &__stat-divider {
      width: 1px;
      height: 32px;
      background: rgba(39, 65, 54, 0.15);
    }

    &__row {
      @extend %layout--flex-row;
      gap: 8px;
    }

    &__tile {
      @extend %layout--flex-col;
      @include border-radius(radius(normal));
      position: relative;
      flex: 1;
      justify-content: space-between;
      gap: 12px;
      padding: 12px space(s);
      min-width: 130px;

      &--vaccines {
        background: #f6f4ff;
        color: #474583;
      }

      &--consultations {
        @include border-radius(8px);
        background: #edf5fe;
        color: #024879;
      }
    }

    &__tile-value-row {
      @extend %text-overflow-ellipsis;
      @extend %layout--flex-row-center-x;
      gap: 6px;
    }

    &__tile-info {
      width: 15px;
      height: 15px;
      flex-shrink: 0;
    }

    &__tile-chevron {
      @include border-radius(radius(circle));
      position: absolute;
      top: 8px;
      right: 8px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      transform: rotate(90deg);
      pointer-events: none;

      img {
        width: 8px;
        height: 12px;
        display: block;
      }
    }

    &__tile--vaccines &__tile-chevron {
      background: #f0edff;
    }

    &__tile--consultations &__tile-chevron {
      background: #e2f0ff;
    }

    &__tile-header,
    &__tile-body {
      @extend %layout--flex-col;
      gap: space(xs);
    }

    &__tile-icon {
      width: 24px;
      height: 24px;
    }

    &__tile--vaccines &__tile-icon {
      transform: rotate(-180deg);
    }

    &__tile-title {
      @extend %text-overflow-ellipsis;
      @extend %smd-font-family-bold;
      font-size: 14px;
      color: inherit;
      text-transform: none;
    }

    &__tile-label {
      @extend %smd-font-family;
      font-size: 10px;
      color: inherit;
      text-transform: none;
    }

    &__tile-value {
      @extend %text-overflow-ellipsis;
      @extend %smd-font-family-bold;
      font-size: font(base);
      text-transform: none;

      &--purple { color: #8f8af9; }
      &--blue { color: #1c9ef9; }
    }

    &__cta {
      @extend %smd-font-family-bold;
      @include border-radius(50px);
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: space(xs);
      max-width: 296px;
      width: 100%;
      height: 48px;
      padding: 14px space(m);
      background: #565af4;
      border: none;
      color: $color-white;
      font-size: font(l) - 1;
      line-height: 20px;
      cursor: pointer;
      text-transform: none;

      &:hover {
        background: darken(#565af4, 4%);
      }
    }

    &__cta-sparkle {
      width: 20px;
      height: 20px;
    }
  }
</style>
